import type {
  CreateTaskRequest,
  MyTaskList,
  TaskDetail,
  TaskLinkRequest,
  TaskList,
  TaskListStatus,
  TaskStatus,
  TaskSummary,
  UpdateTaskRequest,
} from "@esi/shared";
import { uuidSchema } from "@esi/shared";
import {
  type InfiniteData,
  type QueryClient,
  useInfiniteQuery,
  useMutation,
  useMutationState,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect } from "react";
import { csrf, done } from "../account/queries.ts";
import { api } from "../api.ts";
import { ApiError, apiError, ok } from "../api-errors.ts";
import { t } from "../i18n/i18n.ts";
import { projectKeys } from "../projects/queries.ts";
import { parseRoute } from "../router.tsx";
import { useSession } from "../session.tsx";

export type StatusFilter = TaskListStatus | "all";
export interface TaskFilters {
  status: StatusFilter;
  assignee?: string;
  q?: string;
  createdByMe?: boolean;
}
export const taskKeys = {
  all: ["tasks"] as const,
  list: (projectId: string | null, filters: TaskFilters) =>
    ["tasks", "list", projectId, filters] as const,
  detail: (projectId: string, taskId: string) => ["tasks", "detail", projectId, taskId] as const,
  people: (projectId: string, taskId: string | undefined, q: string) =>
    ["tasks", "people", projectId, taskId, q] as const,
};
export function listQuery(filters: TaskFilters, cursor?: string) {
  return {
    ...(filters.status !== "all" && { status: filters.status }),
    ...(filters.assignee && { assignee: filters.assignee }),
    ...(filters.q?.trim() && { q: filters.q.trim() }),
    ...(cursor && { cursor }),
  };
}
export class UnsupportedOpenError extends Error {}
export class AssigneeLookupUnavailableError extends ApiError {}

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}
// Zod 4 serializes its issues in Error.message; older validators exposed issues directly.
function validationIssues(body: unknown) {
  const envelope = record(body);
  if (!envelope || envelope.code !== undefined) {
    return [];
  }
  const error = record(envelope.error);
  let issues = error?.issues ?? envelope.issues;
  if (
    issues === undefined &&
    envelope.success === false &&
    error?.name === "ZodError" &&
    typeof error.message === "string"
  ) {
    try {
      issues = JSON.parse(error.message);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(issues)) {
    return [];
  }
  const parsed = issues.flatMap((value) => {
    const issue = record(value);
    return issue &&
      typeof issue.code === "string" &&
      Array.isArray(issue.path) &&
      issue.path.every((part) => typeof part === "string" || typeof part === "number")
      ? [issue as Record<string, unknown> & { path: (string | number)[] }]
      : [];
  });
  return parsed.length === issues.length ? parsed : [];
}
async function responseIssues(response: { clone(): { json(): Promise<unknown> } }) {
  return validationIssues(
    await response
      .clone()
      .json()
      .catch(() => null),
  );
}

// A gateway can lose the response after committing a write. Keep uncertainty local to tasks.
async function writeResponse<R extends { status: number; clone(): { json(): Promise<unknown> } }>(
  response: R,
): Promise<R> {
  if (response.status >= 500) {
    throw new Error("Uncertain task write");
  }
  if (response.status !== 204) {
    try {
      await response.clone().json();
    } catch {
      throw new Error("Uncertain task write");
    }
  }
  return response;
}
export const denied = (error: unknown) =>
  error instanceof ApiError && (error.status === 403 || error.status === 404);

// A response that started before a revocation must not restore old text when it arrives late.
// The registry holds redacted workflow records only, is scoped to the session's query client,
// and permits content again only after a read started after the redaction.
const redactions = new WeakMap<QueryClient, Map<string, { at: number; task: TaskDetail | null }>>();
const projectRefusals = new WeakMap<QueryClient, Map<string, number>>();
let readOrder = 0;
const taskAddress = (projectId: string, id: string) => `${projectId}/${id}`;
function redactedReads(client: QueryClient) {
  let records = redactions.get(client);
  if (!records) {
    records = new Map();
    redactions.set(client, records);
  }
  return records;
}
function acceptedTask(client: QueryClient, task: TaskSummary, started: number): TaskSummary | null {
  if (started < (projectRefusals.get(client)?.get(task.projectId) ?? 0)) {
    return null;
  }
  const address = taskAddress(task.projectId, task.id);
  const revoked = redactedReads(client).get(address);
  if (task.hidden) {
    return task;
  }
  if (revoked && started < revoked.at) {
    return revoked.task;
  }
  redactedReads(client).delete(address);
  return task;
}
export function safeTaskPages(
  client: QueryClient,
  data: InfiniteData<TaskList>,
): InfiniteData<TaskList> {
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      tasks: page.tasks.flatMap((task) => {
        const record = redactedReads(client).get(taskAddress(task.projectId, task.id));
        return record ? (record.task ? [record.task] : []) : [task];
      }),
    })),
  };
}

/** Replace, never merge, a hidden record. All copies (including inactive pages) are scrubbed. */
export function reconcileTask(
  client: QueryClient,
  projectId: string,
  id: string,
  replacement: TaskDetail | null,
) {
  const previouslyHidden =
    client.getQueryData<TaskDetail | null>(taskKeys.detail(projectId, id))?.hidden === true;
  const records = redactedReads(client);
  if (!replacement || replacement.hidden) {
    records.set(taskAddress(projectId, id), { at: ++readOrder, task: replacement });
  } else {
    records.delete(taskAddress(projectId, id));
  }
  client.setQueriesData<InfiniteData<TaskList>>({ queryKey: ["tasks", "list"] }, (data) =>
    data
      ? {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            tasks: page.tasks.flatMap((task) =>
              task.projectId === projectId && task.id === id
                ? replacement
                  ? [replacement]
                  : []
                : [task],
            ),
          })),
        }
      : data,
  );
  client.setQueryData<TaskDetail | null>(taskKeys.detail(projectId, id), replacement);
  // Lookup names and mutation variables must not retain protected drafts after access loss.
  if (!replacement || (replacement.hidden && !previouslyHidden)) {
    client.removeQueries({ queryKey: ["tasks", "people", projectId] });
    for (const mutation of client.getMutationCache().getAll()) {
      const key = mutation.options.mutationKey;
      // Row status writes (tasks-quick-status) share one key: matched by their task instead. A
      // pending one stays: its variables hold IDs and statuses only, and it keeps the row busy
      // and its status laid over reads until it settles (then gcTime 0 drops it).
      const row =
        key?.[0] === ROW_STATUS_KEY[0] &&
        key[1] === ROW_STATUS_KEY[1] &&
        mutation.state.status !== "pending" &&
        isRowChangeOf(mutation.state.variables, projectId, id);
      if ((key?.[0] === "tasks" && key[1] === projectId && key[2] === id) || row) {
        client.getMutationCache().remove(mutation);
      }
    }
  }
}
export function removeProjectTasks(client: QueryClient, projectId: string) {
  let refused = projectRefusals.get(client);
  if (!refused) {
    refused = new Map();
    projectRefusals.set(client, refused);
  }
  refused.set(projectId, ++readOrder);
  for (const query of client.getQueryCache().findAll({ queryKey: ["tasks", "list"] })) {
    const data = query.state.data as InfiniteData<TaskList> | undefined;
    for (const task of data?.pages.flatMap((page) => page.tasks) ?? []) {
      if (task.projectId === projectId) {
        reconcileTask(client, projectId, task.id, null);
      }
    }
  }
  for (const query of client
    .getQueryCache()
    .findAll({ queryKey: ["tasks", "detail", projectId] })) {
    const id = query.queryKey[3];
    if (typeof id === "string") {
      reconcileTask(client, projectId, id, null);
    }
  }
  client.setQueriesData<InfiniteData<TaskList>>({ queryKey: ["tasks", "list"] }, (data) =>
    data
      ? {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            tasks: page.tasks.filter((task) => task.projectId !== projectId),
          })),
        }
      : data,
  );
  client.removeQueries({ queryKey: ["tasks", "people", projectId] });
}
/**
 * A refetch that started while a row status write is still out must not put that row's old
 * status back: pending writes stay laid over every page read (the write's own answer, then its
 * refresh, settle it).
 */
function overlayPendingRowStatus(client: QueryClient, page: TaskList) {
  const pending = client
    .getMutationCache()
    .findAll({ mutationKey: ROW_STATUS_KEY, status: "pending" })
    .map((m) => m.state.variables as RowStatusChange | undefined)
    // A write whose answer is in reads for its own refresh, not its optimistic value: the
    // mutation is still "pending" while its onSettled refresh runs.
    .filter((c) => c && !answeredRowChanges.has(c));
  if (pending.length === 0) {
    return;
  }
  page.tasks = page.tasks.map((task) => {
    const change = pending.findLast((c) => c && isRowChangeOf(c, task.projectId, task.id));
    if (!change) {
      return task;
    }
    // A restricted row is re-read from its redacted record (`safeTaskPages`): it carries the
    // pending status too, its text still redacted.
    setRowStatus(client, change, change.status, () => true, { listsToo: false });
    return { ...task, status: change.status };
  });
}
function reconcilePage(client: QueryClient, page: TaskList, started: number) {
  page.tasks = page.tasks.flatMap((task) => {
    const accepted = acceptedTask(client, task, started);
    if (accepted?.hidden) {
      const key = taskKeys.detail(accepted.projectId, accepted.id);
      const detail = client.getQueryData<TaskDetail | null>(key);
      reconcileTask(client, accepted.projectId, accepted.id, accepted);
      // A list summary cannot authorize workflow actions. Scrub first, then read named detail.
      if (detail) {
        void client.invalidateQueries({ queryKey: key, exact: true });
      }
    }
    return accepted ? [accepted] : [];
  });
}
export async function fetchTasks(
  client: QueryClient,
  projectId: string | null,
  filters: TaskFilters,
  cursor?: string,
  signal?: AbortSignal,
): Promise<MyTaskList | TaskList> {
  const started = ++readOrder;
  const query = listQuery(filters, cursor);
  const response = projectId
    ? await api.api.projects[":projectId"].tasks.$get(
        { param: { projectId }, query },
        { init: { signal } },
      )
    : await api.api.me.tasks.$get(
        {
          query: {
            ...(filters.status !== "all" && { status: filters.status }),
            createdByMe: filters.createdByMe ? "true" : "false",
            ...(cursor && { cursor }),
          },
        },
        { init: { signal } },
      );
  signal?.throwIfAborted();
  if (!response.ok) {
    if (!cursor && filters.status === "open" && response.status === 400) {
      const issues = await responseIssues(response);
      signal?.throwIfAborted();
      if (issues.some((issue) => issue.path.length === 1 && issue.path[0] === "status")) {
        throw new UnsupportedOpenError();
      }
    }
    const error = await apiError(response);
    signal?.throwIfAborted();
    if (projectId && denied(error)) {
      removeProjectTasks(client, projectId);
    }
    throw error;
  }
  const page = await ok(response);
  signal?.throwIfAborted();
  reconcilePage(client, page, started);
  overlayPendingRowStatus(client, page);
  return page;
}
export function useTaskPages(projectId: string | null, filters: TaskFilters) {
  const client = useQueryClient();
  return useInfiniteQuery({
    queryKey: taskKeys.list(projectId, filters),
    structuralSharing: (_old, data) => safeTaskPages(client, data as InfiniteData<TaskList>),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) => fetchTasks(client, projectId, filters, pageParam, signal),
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
  });
}
export async function fetchTask(
  client: QueryClient,
  projectId: string,
  taskId: string,
  signal?: AbortSignal,
) {
  const started = ++readOrder;
  try {
    const task = await ok(
      await api.api.projects[":projectId"].tasks[":taskId"].$get(
        { param: { projectId, taskId } },
        { init: { signal } },
      ),
    );
    signal?.throwIfAborted();
    const accepted = acceptedTask(client, task, started) as TaskDetail | null;
    // A stale visible response cannot overrule a newer hidden/denied one.
    if (accepted && !accepted.hidden) {
      reconcileTask(client, projectId, taskId, accepted);
    } else if (task.hidden) {
      reconcileTask(client, projectId, taskId, task);
    }
    return accepted;
  } catch (error) {
    signal?.throwIfAborted();
    if (denied(error)) {
      reconcileTask(client, projectId, taskId, null);
      void client.invalidateQueries({ queryKey: ["tasks", "list"] });
    }
    throw error;
  }
}
export function useTask(projectId: string, taskId: string) {
  const client = useQueryClient();
  return useQuery({
    queryKey: taskKeys.detail(projectId, taskId),
    queryFn: ({ signal }) => fetchTask(client, projectId, taskId, signal),
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
  });
}
export async function refreshTasks(client: QueryClient, projectId: string) {
  await Promise.all([
    client.invalidateQueries({ queryKey: taskKeys.all }),
    client.invalidateQueries({ queryKey: projectKeys.project(projectId), exact: true }),
    client.invalidateQueries({ queryKey: ["admin", "projects"] }),
  ]);
}
export function useTaskFocus() {
  const client = useQueryClient();
  useEffect(() => {
    const refresh = () => {
      void client.invalidateQueries({ queryKey: taskKeys.all });
    };
    window.addEventListener("focus", refresh);
    // A project refusal can unmount the detail before its own read returns. Scrub tasks and
    // the document title at that boundary too, while older in-flight reads remain guarded.
    const unsubscribe = client.getQueryCache().subscribe(({ query }) => {
      if (
        query.queryKey[0] !== "projects" ||
        query.queryKey.length !== 2 ||
        !denied(query.state.error)
      ) {
        return;
      }
      const projectId = query.queryKey[1];
      if (typeof projectId !== "string") {
        return;
      }
      removeProjectTasks(client, projectId);
      const route = parseRoute(window.location.pathname);
      if (route.name === "projectTask" && route.projectId === projectId) {
        document.title = `${t("tasks.unavailable")} · ${t("app.name")}`;
      }
    });
    return () => {
      window.removeEventListener("focus", refresh);
      unsubscribe();
    };
  }, [client]);
}
export type TaskWrite =
  | { kind: "create"; data: CreateTaskRequest }
  | { kind: "edit"; data: UpdateTaskRequest }
  | { kind: "delete" }
  | { kind: "comment"; body: string }
  | { kind: "deleteComment"; id: string }
  | { kind: "link"; data: TaskLinkRequest }
  | { kind: "deleteLink"; id: string };
export function useTaskWrite(projectId: string, taskId = "") {
  const client = useQueryClient();
  const { csrfToken } = useSession();
  const mutation = useMutation({
    mutationKey: ["tasks", projectId, taskId],
    retry: false,
    gcTime: 0,
    mutationFn: async (input: TaskWrite) => {
      const route = api.api.projects[":projectId"].tasks;
      const param = { projectId, taskId };
      const options = csrf(csrfToken);
      switch (input.kind) {
        case "create":
          return ok(
            await writeResponse(
              await route.$post({ param: { projectId }, json: input.data }, options),
            ),
          );
        case "edit":
          return ok(
            await writeResponse(
              await route[":taskId"].$patch({ param, json: input.data }, options),
            ),
          );
        case "delete":
          await done(await writeResponse(await route[":taskId"].$delete({ param }, options)));
          reconcileTask(client, projectId, taskId, null);
          // Its conversations' headers drop the task chip (tasks-conversations).
          await client.invalidateQueries({ queryKey: ["conversation"] });
          return;
        case "comment":
          return ok(
            await writeResponse(
              await route[":taskId"].comments.$post({ param, json: { body: input.body } }, options),
            ),
          );
        case "deleteComment":
          return done(
            await writeResponse(
              await route[":taskId"].comments[":commentId"].$delete(
                { param: { ...param, commentId: input.id } },
                options,
              ),
            ),
          );
        case "link":
          return ok(
            await writeResponse(
              await route[":taskId"].links.$post({ param, json: input.data }, options),
            ),
          );
        case "deleteLink":
          return done(
            await writeResponse(
              await route[":taskId"].links[":linkId"].$delete(
                { param: { ...param, linkId: input.id } },
                options,
              ),
            ),
          );
      }
    },
    onSettled: async () => {
      await refreshTasks(client, projectId);
    },
  });
  return mutation;
}
/**
 * Whether the browser has redacted a task's text since loading it (hidden, refused or gone): a
 * notice built from an older answer must not show that text again.
 */
export function taskTextRedacted(client: QueryClient, projectId: string, taskId: string) {
  return redactedReads(client).has(taskAddress(projectId, taskId));
}
/** Whether a list filtered on `filter` keeps a task of `status`. */
export function statusMatches(filter: StatusFilter, status: TaskStatus): boolean {
  return filter === "all" || (filter === "open" ? status !== "done" : status === filter);
}
export interface RowStatusChange {
  projectId: string;
  taskId: string;
  status: TaskStatus;
  /** The status the row showed before: what a rollback and Undo put back. */
  from: TaskStatus;
}
/** What a row write keeps of its answer: never text. */
export interface RowStatusResult {
  status: TaskStatus;
  hidden: boolean;
}
const ROW_STATUS_KEY = ["tasks", "row-status"] as const;
/** Row changes whose write has answered (success or failure); weakly held. */
const answeredRowChanges = new WeakSet<RowStatusChange>();
function isRowChangeOf(variables: unknown, projectId: string, taskId: string): boolean {
  const change = variables as Partial<RowStatusChange> | undefined;
  return change?.projectId === projectId && change.taskId === taskId;
}
/** Sets a task's status in every cached list, where `when` holds for its current status. */
function setRowStatus(
  client: QueryClient,
  change: Pick<RowStatusChange, "projectId" | "taskId">,
  status: TaskStatus,
  when: (current: TaskStatus) => boolean = () => true,
  options: { listsToo?: boolean } = {},
) {
  // A restricted row is re-read from its redacted record (`safeTaskPages`): its workflow status
  // follows there too, so the record never puts the old one back. Text stays redacted.
  const records = redactedReads(client);
  const address = taskAddress(change.projectId, change.taskId);
  const record = records.get(address);
  if (record?.task && when(record.task.status)) {
    records.set(address, { ...record, task: { ...record.task, status } });
  }
  if (options.listsToo === false) {
    return;
  }
  client.setQueriesData<InfiniteData<TaskList>>({ queryKey: ["tasks", "list"] }, (data) =>
    data
      ? {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            tasks: page.tasks.map((task) =>
              task.projectId === change.projectId && task.id === change.taskId && when(task.status)
                ? { ...task, status }
                : task,
            ),
          })),
        }
      : data,
  );
}
/**
 * Status from a task row (tasks-quick-status): the detail's status-only PATCH, shown at once in
 * every cached list and put back on a refusal or a failure. Never retried; the lists refresh
 * whatever the outcome, so a new write block or a lost assignment reaches the row.
 */
export function useRowStatus() {
  const client = useQueryClient();
  const { csrfToken } = useSession();
  return useMutation({
    mutationKey: ROW_STATUS_KEY,
    retry: false,
    // Its answer is a full task: like every task write, never kept once settled.
    gcTime: 0,
    // The answer is a full task; the mutation keeps only its workflow outcome, so no task text
    // ever sits in the mutation cache, whatever access does next.
    mutationFn: async (change: RowStatusChange): Promise<RowStatusResult> => {
      const detail = await ok(
        await writeResponse(
          await api.api.projects[":projectId"].tasks[":taskId"].$patch(
            {
              param: { projectId: change.projectId, taskId: change.taskId },
              json: { status: change.status },
            },
            csrf(csrfToken),
          ),
        ),
      );
      if (detail.hidden) {
        // Text rights may have gone with the write's answer: scrub as any hidden read does.
        reconcileTask(client, change.projectId, change.taskId, detail);
      }
      return { status: detail.status, hidden: detail.hidden };
    },
    onMutate: async (change) => {
      // Only the lists showing this task: an unrelated list or page keeps loading.
      await client.cancelQueries({
        queryKey: ["tasks", "list"],
        predicate: (query) =>
          (query.state.data as InfiniteData<TaskList> | undefined)?.pages.some((page) =>
            page.tasks.some((t) => t.projectId === change.projectId && t.id === change.taskId),
          ) === true,
      });
      setRowStatus(client, change, change.status);
    },
    onSuccess: (result, change) => {
      answeredRowChanges.add(change);
      setRowStatus(client, change, result.status);
    },
    onError: (_error, change) => {
      answeredRowChanges.add(change);
      // Only a row still showing this change goes back; a newer read or write wins.
      setRowStatus(client, change, change.from, (current) => current === change.status);
    },
    onSettled: async (_task, _error, change) => {
      await refreshTasks(client, change.projectId);
    },
  });
}
/** The tasks whose row status write is in flight: their rows stay until it settles. */
export function usePendingRowStatus(): ReadonlySet<string> {
  const pending = useMutationState({
    filters: { mutationKey: ROW_STATUS_KEY, status: "pending" },
    select: (mutation) => (mutation.state.variables as RowStatusChange | undefined)?.taskId,
  });
  return new Set(pending.filter((id): id is string => Boolean(id)));
}
/**
 * Work on this with ESI (tasks-conversations): one POST, never retried (each start creates a
 * conversation). The task and conversation lists are refreshed whatever the outcome, so a refusal
 * (frozen, tasks off, access lost) updates the detail's actions.
 */
export function useStartTaskConversation(projectId: string, taskId: string) {
  const client = useQueryClient();
  const { csrfToken } = useSession();
  return useMutation({
    mutationKey: ["tasks", projectId, taskId, "conversation"],
    retry: false,
    gcTime: 0,
    mutationFn: async () => {
      const response = await api.api.projects[":projectId"].tasks[":taskId"].conversations.$post(
        { param: { projectId, taskId } },
        csrf(csrfToken),
      );
      return ok(await writeResponse(response));
    },
    onSettled: async () => {
      await Promise.all([
        refreshTasks(client, projectId),
        client.invalidateQueries({ queryKey: ["conversations"] }),
      ]);
    },
  });
}
export async function lookupAssignees(
  projectId: string,
  q: string,
  taskId?: string,
  signal?: AbortSignal,
) {
  const response = await api.api.projects[":projectId"].tasks.assignees.$get(
    {
      param: { projectId },
      query: { q, ...(taskId && { taskId }) },
    },
    { init: { signal } },
  );
  signal?.throwIfAborted();
  if (response.status === 400) {
    const issues = await responseIssues(response);
    signal?.throwIfAborted();
    // The old /:taskId route treats the literal "assignees" as an invalid UUID.
    // A supplied taskId must itself be valid, so its query validation is not mistaken for this.
    const validTaskId = !taskId || uuidSchema.safeParse(taskId).success;
    if (
      validTaskId &&
      issues.length === 1 &&
      issues[0]?.path.length === 1 &&
      issues[0].path[0] === "taskId" &&
      issues[0].code === "invalid_format" &&
      issues[0].format === "uuid"
    ) {
      throw new AssigneeLookupUnavailableError(400, t("tasks.lookupUnavailable"));
    }
  }
  return ok(response);
}
export type { TaskSummary };
